import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import * as Icon from "react-bootstrap-icons";
import { toast } from "react-toastify";
import "../styles/excel.css";
import { defaultCellData } from './spreadsheet/defaultCellData';
import { buildBorderObj, borderCSS, diagonalCSS, BORDER_UI, BORDER_STYLES, BORDER_WIDTHS } from './spreadsheet/borderEngine';
import { createTable, TABLE_STYLE_NAMES, getTableStyle, isInTable, autoExpandTable, removeTableDuplicates } from './spreadsheet/tableEngine';
import { sortRows, filterRows, SORT_ASC, SORT_DESC } from './spreadsheet/sortFilterEngine';
import { cfRule, evaluateCF, getDataBarStyle, getColorScaleStyle, getIconSetStyle } from './spreadsheet/conditionalFormatEngine';
import axios from "../utils/axiosInstance";

const API = process.env.REACT_APP_API_URL || "";

// --- Constants ---
const ROWS = 100, COLS = 26;
const DEFAULT_COL_WIDTH = 100, DEFAULT_ROW_HEIGHT = 24;
const HEADER_H = 24, ROW_HEADER_W = 50;
const MAX_COL_WIDTH = 500, MIN_COL_WIDTH = 30;
const MAX_ROW_HEIGHT = 200, MIN_ROW_HEIGHT = 18;

function colLabel(c) {
  let label = '', n = c;
  while (n >= 0) { label = String.fromCharCode(65 + (n % 26)) + label; n = Math.floor(n / 26) - 1; }
  return label;
}
function cellId(r, c) { return `${r}-${c}`; }

const FONT_SIZES = [8,9,10,11,12,14,16,18,20,24,28,36,48,72];
const FONTS = ['Calibri','Arial','Times New Roman','Courier New','Verdana','Georgia','Segoe UI','Tahoma'];
const THEME_COLORS = [
  '#FFFFFF','#000000','#EEECE1','#1F4E79','#2E75B6','#00B0F0','#00B050','#92D050',
  '#FFFF00','#FFC000','#FF0000','#C00000','#7030A0','#44546A','#4472C4','#5B9BD5',
  '#A5A5A5','#7F7F7F','#D9E2F3','#BDD7EE','#E2EFDA','#C6EFCE','#FFEB9C','#FCE4D6',
  '#D9D9D9','#C0C0C0','#F2F2F2','#D6DCE4','#DEEBF7','#E2F0D9','#F2F2F2','#FFF2CC',
];
const NUMBER_FORMATS = [
  {label:'General',value:'general'},{label:'Number',value:'number'},
  {label:'Currency',value:'currency'},{label:'Accounting',value:'accounting'},
  {label:'Percentage',value:'percentage'},{label:'Date',value:'date'},{label:'Text',value:'text'},
];

function formatCellValue(val, format) {
  if (val === '' || val === null || val === undefined) return '';
  if (format === 'general') return val;
  if (format === 'number') { const n = parseFloat(val); return isNaN(n) ? val : n.toLocaleString(undefined, {minimumFractionDigits:0,maximumFractionDigits:2}); }
  if (format === 'currency') { const n = parseFloat(val); return isNaN(n) ? val : '$ ' + n.toLocaleString(undefined, {minimumFractionDigits:2,maximumFractionDigits:2}); }
  if (format === 'accounting') { const n = parseFloat(val); if (isNaN(n)) return val; return n < 0 ? `($${Math.abs(n).toFixed(2)})` : `$ ${n.toFixed(2)} `; }
  if (format === 'percentage') { const n = parseFloat(val); return isNaN(n) ? val : (n * 100).toFixed(2) + '%'; }
  if (format === 'date') { const d = new Date(val); if (isNaN(d.getTime())) return val; return d.toLocaleDateString(); }
  return val;
}

function getFillValues(sourceRange, sourceData) {
  const vals = [];
  for (let r = sourceRange.startRow; r <= sourceRange.endRow; r++) {
    const row = [];
    for (let c = sourceRange.startCol; c <= sourceRange.endCol; c++) row.push(sourceData[cellId(r,c)] || defaultCellData());
    vals.push(row);
  }
  return vals;
}

function getHAlign(align) {
  if (align === 'left') return {justifyContent:'flex-start',textAlign:'left'};
  if (align === 'center') return {justifyContent:'center',textAlign:'center'};
  if (align === 'right') return {justifyContent:'flex-end',textAlign:'right'};
  return {justifyContent:'flex-start',textAlign:'left'};
}
function getVAlign(align) {
  if (align === 'top') return {alignItems:'flex-start'};
  if (align === 'center') return {alignItems:'center'};
  return {alignItems:'flex-end'};
}

// --- Default cell data with extended border props ---
function makeCellData() {
  return {
    ...defaultCellData(),
    borderTop:false, borderBottom:false, borderLeft:false, borderRight:false,
    borderColor:'#000000', borderStyle:'solid',
    borderTopStyle:'solid', borderBottomStyle:'solid', borderLeftStyle:'solid', borderRightStyle:'solid',
    borderTopColor:'#000000', borderBottomColor:'#000000', borderLeftColor:'#000000', borderRightColor:'#000000',
    borderTopWidth:1, borderBottomWidth:1, borderLeftWidth:1, borderRightWidth:1,
    diagTR:false, diagBL:false, diagColor:'#000000', diagStyle:'solid', diagWidth:1,
    wrapText:false, indent:0,
  };
}

export default function Spreadsheet({ reportId, onSave, onBack }) {
  // --- Data ---
  const [gridData, setGridData] = useState(() => {
    const d = {};
    for (let r = 0; r < ROWS; r++)
      for (let c = 0; c < COLS; c++)
        d[cellId(r,c)] = makeCellData();
    return d;
  });
  const [colWidths, setColWidths] = useState(() => Array(COLS).fill(DEFAULT_COL_WIDTH));
  const [rowHeights, setRowHeights] = useState(() => Array(ROWS).fill(DEFAULT_ROW_HEIGHT));

  // --- Selection ---
  const [activeCell, setActiveCell] = useState({row:0,col:0});
  const [selectionStart, setSelectionStart] = useState({row:0,col:0});
  const [selectionEnd, setSelectionEnd] = useState({row:0,col:0});
  const [isSelecting, setIsSelecting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  // --- Tables ---
  const [tables, setTables] = useState([]);
  const [tableDialog, setTableDialog] = useState(null);
  const [showTableDesign, setShowTableDesign] = useState(false);

  // --- Sort/Filter ---
  const [sorts, setSorts] = useState([]);
  const [filters, setFilters] = useState({});
  const [filterDropdown, setFilterDropdown] = useState(null);

  // --- Conditional Formatting ---
  const [cfRules, setCfRules] = useState([]);
  const [cfDialog, setCfDialog] = useState(null);

  // --- API save/load for report integration ---
  const saveToApi = useCallback(() => {
    const payload = {
      gridData, colWidths, rowHeights,
      tables, sorts, filters, cfRules,
    };
    onSave?.({ content: JSON.stringify(payload), report_type: "excel" });
  }, [gridData, colWidths, rowHeights, tables, sorts, filters, cfRules, onSave]);

  useEffect(() => {
    if (reportId) {
      axios.get(`${API}/api/amaraporo/${reportId}`).then(res => {
        try {
          const data = JSON.parse(res.data.content);
          if (data.gridData) setGridData(prev => ({...prev, ...data.gridData}));
          if (data.colWidths) setColWidths(data.colWidths);
          if (data.rowHeights) setRowHeights(data.rowHeights);
          if (data.tables) setTables(data.tables);
          if (data.sorts) setSorts(data.sorts);
          if (data.filters) setFilters(data.filters);
          if (data.cfRules) setCfRules(data.cfRules);
        } catch {}
      }).catch(() => toast.error("Failed to load report"));
    }
  }, [reportId]);

  // --- UI ---
  const [activeRibbonTab, setActiveRibbonTab] = useState('home');
  const [sheets, setSheets] = useState([{name:'Sheet1',id:0}]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [statusText, setStatusText] = useState('Ready');
  const [zoom, setZoom] = useState(100);
  const [contextMenu, setContextMenu] = useState(null);
  const [textColorPicker, setTextColorPicker] = useState(null);
  const [fillColorPicker, setFillColorPicker] = useState(null);
  const [borderPicker, setBorderPicker] = useState(null);
  const [advancedBorderDialog, setAdvancedBorderDialog] = useState(null);
  const [borderLineStyle, setBorderLineStyle] = useState('solid');
  const [borderLineColor, setBorderLineColor] = useState('#000000');
  const [borderLineWidth, setBorderLineWidth] = useState(1);
  const [borderTool, setBorderTool] = useState('none'); // 'none' | 'draw' | 'erase'
  const [showGridlines, setShowGridlines] = useState(true);

  // --- Refs ---
  const gridRef = useRef(null);
  const editRef = useRef(null);
  const selectingRef = useRef(false);
  const fillDragging = useRef(false);
  const colResizing = useRef(null);
  const rowResizing = useRef(null);
  const containerRef = useRef(null);
  const contextMenuRef = useRef(null);
  const activeCellRef = useRef(activeCell);
  activeCellRef.current = activeCell;
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const editValueRef = useRef(editValue);
  editValueRef.current = editValue;

  // --- Scroll ---
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [visibleRows, setVisibleRows] = useState(30);
  const [visibleCols, setVisibleCols] = useState(10);

  const activeRange = useMemo(() => ({
    startRow: Math.min(selectionStart.row, selectionEnd.row),
    endRow: Math.max(selectionStart.row, selectionEnd.row),
    startCol: Math.min(selectionStart.col, selectionEnd.col),
    endCol: Math.max(selectionStart.col, selectionEnd.col),
  }), [selectionStart, selectionEnd]);

  const isSelected = useCallback((r,c) => {
    const range = activeRange;
    return r >= range.startRow && r <= range.endRow && c >= range.startCol && c <= range.endCol;
  }, [activeRange]);

  // --- Resize observer ---
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(entries => {
      const rect = entries[0].contentRect;
      setVisibleRows(Math.max(10, Math.ceil(rect.height / DEFAULT_ROW_HEIGHT)));
      setVisibleCols(Math.max(5, Math.ceil(rect.width / DEFAULT_COL_WIDTH)));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleScroll = useCallback(e => {
    setScrollLeft(e.target.scrollLeft);
    setScrollTop(e.target.scrollTop);
  }, []);

  // --- Cell data access ---
  const getCellData = useCallback((r,c) => gridData[cellId(r,c)] || makeCellData(), [gridData]);

  const updateCell = useCallback((r,c,updates) => {
    setGridData(prev => ({...prev, [cellId(r,c)]: {...(prev[cellId(r,c)] || makeCellData()), ...updates}}));
  }, []);

  const updateSelectedCells = useCallback((updates) => {
    const range = activeRange;
    setGridData(prev => {
      const next = {...prev};
      for (let r = range.startRow; r <= range.endRow; r++)
        for (let c = range.startCol; c <= range.endCol; c++)
          next[cellId(r,c)] = {...(next[cellId(r,c)] || makeCellData()), ...updates};
      return next;
    });
  }, [activeRange]);

  const selectAll = useCallback(() => {
    setSelectionStart({row:0,col:0}); setSelectionEnd({row:ROWS-1,col:COLS-1}); setActiveCell({row:0,col:0});
  }, []);

  // --- Table auto-expand & Border draw/erase tool ---
  const handleTableAutoExpand = useCallback((r, c) => {
    setTables(prev => autoExpandTable(prev, r, c));
  }, []);

  const applyBorderTool = useCallback((row, col) => {
    if (borderTool === 'draw') {
      const obj = buildBorderObj({
        preset: 'all', style: borderLineStyle, color: borderLineColor, width: borderLineWidth,
      });
      setGridData(prev => {
        const key = cellId(row, col);
        return { ...prev, [key]: { ...(prev[key] || makeCellData()), ...obj } };
      });
    } else if (borderTool === 'erase') {
      setGridData(prev => {
        const key = cellId(row, col);
        return { ...prev, [key]: { ...(prev[key] || makeCellData()),
          borderTop: false, borderBottom: false, borderLeft: false, borderRight: false,
          diagTR: false, diagBL: false,
        } };
      });
    }
  }, [borderTool, borderLineStyle, borderLineColor, borderLineWidth]);

  // --- Mouse handlers ---
  const handleCellMouseDown = useCallback((e, row, col) => {
    if (e.button !== 0) return;
    e.preventDefault();
    if (editing && activeCell) {
      updateCell(activeCell.row, activeCell.col, {value: editValue});
      handleTableAutoExpand(activeCell.row, activeCell.col);
      setEditing(false);
    }
    if (borderTool !== 'none') {
      applyBorderTool(row, col);
      selectingRef.current = true;
      return;
    }
    if (e.shiftKey) { setSelectionEnd({row,col}); }
    else { selectingRef.current = true; setIsSelecting(true); setSelectionStart({row,col}); setSelectionEnd({row,col}); setActiveCell({row,col}); }
  }, [editing, editValue, activeCell, updateCell, handleTableAutoExpand, borderTool, applyBorderTool]);

  const handleCellMouseEnter = useCallback((row, col) => {
    if (borderTool !== 'none' && selectingRef.current) {
      applyBorderTool(row, col);
      return;
    }
    if (selectingRef.current && isSelecting) setSelectionEnd({row,col});
  }, [isSelecting, borderTool, applyBorderTool, selectingRef]);

  const handleMouseUp = useCallback(() => {
    if (selectingRef.current) { selectingRef.current = false; setIsSelecting(false); }
    if (colResizing.current) colResizing.current = null;
    if (rowResizing.current) rowResizing.current = null;
    if (fillDragging.current) fillDragging.current = false;
  }, []);

  useEffect(() => { window.addEventListener('mouseup', handleMouseUp); return () => window.removeEventListener('mouseup', handleMouseUp); }, [handleMouseUp]);

  const handleCellDoubleClick = useCallback((row, col) => {
    const cell = getCellData(row, col);
    setActiveCell({row,col}); setSelectionStart({row,col}); setSelectionEnd({row,col});
    setEditValue(cell.value); setEditing(true); setStatusText('Edit');
  }, [getCellData]);

  // --- Edit handlers ---
  const handleEditChange = useCallback(e => setEditValue(e.target.value), []);
  const commitEdit = useCallback(() => {
    if (editing && activeCell) {
      updateCell(activeCell.row, activeCell.col, {value: editValue});
      handleTableAutoExpand(activeCell.row, activeCell.col);
      setEditing(false); setStatusText('Ready');
    }
  }, [editing, activeCell, editValue, updateCell, handleTableAutoExpand]);

  const handleEditKeyDown = useCallback(e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); commitEdit();
      const nr = Math.min(ROWS-1, activeCell.row+1); setActiveCell({row:nr,col:activeCell.col}); setSelectionStart({row:nr,col:activeCell.col}); setSelectionEnd({row:nr,col:activeCell.col});
    } else if (e.key === 'Escape') { e.preventDefault(); setEditing(false); setStatusText('Ready'); }
    else if (e.key === 'Tab') {
      e.preventDefault(); commitEdit();
      const nc = e.shiftKey ? activeCell.col-1 : activeCell.col+1;
      if (nc >= 0 && nc < COLS) { setActiveCell({row:activeCell.row,col:nc}); setSelectionStart({row:activeCell.row,col:nc}); setSelectionEnd({row:activeCell.row,col:nc}); }
    }
  }, [activeCell, commitEdit]);

  useEffect(() => { if (editing && editRef.current) editRef.current.focus(); }, [editing]);

  // --- Keyboard ---
  // --- Copy/Paste ---
  const handleCopy = useCallback(() => {
    const range = activeRange;
    const rows = [];
    for (let r = range.startRow; r <= range.endRow; r++) { const row = []; for (let c = range.startCol; c <= range.endCol; c++) row.push(getCellData(r,c).value); rows.push(row.join('\t')); }
    navigator.clipboard.writeText(rows.join('\n')).catch(()=>{});
  }, [activeRange, getCellData]);

  const handlePaste = useCallback(() => {
    navigator.clipboard.readText().then(text => {
      if (!text.trim()) return;
      const rows = text.split('\n').map(l => l.split('\t'));
      const sr = activeCell.row, sc = activeCell.col;
      setGridData(prev => {
        const next = {...prev};
        for (let r = 0; r < rows.length; r++)
          for (let c = 0; c < rows[r].length; c++) {
            const tr = sr + r, tc = sc + c;
            if (tr < ROWS && tc < COLS) next[cellId(tr,tc)] = {...(next[cellId(tr,tc)] || makeCellData()), value: rows[r][c]};
          }
        return next;
      });
    }).catch(()=>{});
  }, [activeCell]);

  // --- Table Creation ---
  const handleCreateTable = useCallback(() => {
    const range = activeRange;
    setTableDialog({
      startRow: range.startRow, startCol: range.startCol,
      endRow: range.endRow, endCol: range.endCol,
      name: `Table${tables.length + 1}`,
      style: TABLE_STYLE_NAMES[0],
      headerRow: true,
    });
  }, [activeRange, tables.length]);

  const confirmCreateTable = useCallback(() => {
    if (!tableDialog) return;
    const t = createTable(tableDialog.name, tableDialog.startRow, tableDialog.startCol, tableDialog.endRow, tableDialog.endCol, tableDialog.style);
    t.headerRow = tableDialog.headerRow;
    setTables(prev => [...prev, t]);
    setTableDialog(null);
    setStatusText(`Table "${t.name}" created`);
    // Mark header row cells
    if (t.headerRow) {
      for (let c = t.startCol; c <= t.endCol; c++) {
        const cell = gridData[cellId(t.startRow, c)];
        if (cell && !cell.value) updateCell(t.startRow, c, {value: colLabel(c)});
      }
    }
  }, [tableDialog, gridData, updateCell]);

  const convertTableToRange = useCallback((tblIdx) => {
    setTables(prev => prev.filter((_, i) => i !== tblIdx));
    setShowTableDesign(false);
  }, []);

  const applyTableStyle = useCallback((tblIdx, styleName) => {
    setTables(prev => prev.map((t, i) => i === tblIdx ? {...t, style: styleName} : t));
  }, []);

  // --- Table auto-expand & context mode ---
  useEffect(() => {
    const idx = isInTable(tables, activeCell.row, activeCell.col);
    setShowTableDesign(idx >= 0);
  }, [activeCell, tables]);

  // --- Ribbon formatting ---
  const toggleBold = useCallback(() => { const range = activeRange; const first = getCellData(range.startRow,range.startCol); updateSelectedCells({bold: !first.bold}); }, [activeRange, getCellData, updateSelectedCells]);
  const toggleItalic = useCallback(() => { const range = activeRange; const first = getCellData(range.startRow,range.startCol); updateSelectedCells({italic: !first.italic}); }, [activeRange, getCellData, updateSelectedCells]);
  const toggleUnderline = useCallback(() => { const range = activeRange; const first = getCellData(range.startRow,range.startCol); updateSelectedCells({underline: !first.underline}); }, [activeRange, getCellData, updateSelectedCells]);
  const setCellFont = useCallback(font => updateSelectedCells({fontFamily: font}), [updateSelectedCells]);
  const setCellFontSize = useCallback(size => updateSelectedCells({fontSize: parseInt(size)}), [updateSelectedCells]);
  const setCellColor = useCallback(color => { updateSelectedCells({color}); setTextColorPicker(null); }, [updateSelectedCells]);
  const setCellBgColor = useCallback(color => { updateSelectedCells({bgColor: color === '#FFFFFF' ? '' : color}); setFillColorPicker(null); }, [updateSelectedCells]);
  const setCellHAlign = useCallback(align => updateSelectedCells({hAlign: align}), [updateSelectedCells]);
  const setCellVAlign = useCallback(align => updateSelectedCells({vAlign: align}), [updateSelectedCells]);
  const setCellNumberFormat = useCallback(fmt => updateSelectedCells({numberFormat: fmt}), [updateSelectedCells]);
  const toggleWrapText = useCallback(() => { const range = activeRange; const first = getCellData(range.startRow,range.startCol); updateSelectedCells({wrapText: !first.wrapText}); }, [activeRange, getCellData, updateSelectedCells]);

  // --- Keyboard ---
  const handleKeyDown = useCallback(e => {
    if (editing) { handleEditKeyDown(e); return; }
    let {row,col} = activeCell;
    let handled = true;
    if (e.key === 'ArrowUp') { row = Math.max(0, row-1); }
    else if (e.key === 'ArrowDown') { row = Math.min(ROWS-1, row+1); }
    else if (e.key === 'ArrowLeft') { col = Math.max(0, col-1); }
    else if (e.key === 'ArrowRight') { col = Math.min(COLS-1, col+1); }
    else if (e.key === 'Enter') { e.preventDefault(); handleCellDoubleClick(row,col); return; }
    else if (e.key === 'Tab') { col = Math.min(COLS-1, col+1); }
    else if (e.key === 'Home') { col = 0; }
    else if (e.key === 'End') { col = COLS-1; }
    else if (e.key === 'PageDown') { row = Math.min(ROWS-1, row+20); }
    else if (e.key === 'PageUp') { row = Math.max(0, row-20); }
    else if (e.key === 'Delete') { e.preventDefault(); updateSelectedCells(makeCellData()); return; }
    else if (e.key === 't' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleCreateTable(); return; }
    else if (e.key === 'a' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); selectAll(); return; }
    else if (e.key === 'c' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleCopy(); return; }
    else if (e.key === 'v' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handlePaste(); return; }
    else if (e.key === 'z' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); return; }
    else if (e.key === 'b' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); toggleBold(); return; }
    else if (e.key === 'i' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); toggleItalic(); return; }
    else if (e.key === 'u' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); toggleUnderline(); return; }
    else if (e.key === 'l' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); return; }
    else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) { setEditValue(e.key); setEditing(true); setStatusText('Edit'); return; }
    else if (e.key === 'F2') { e.preventDefault(); handleCellDoubleClick(row,col); return; }
    else handled = false;
    if (handled) {
      e.preventDefault();
      if (e.shiftKey) { setSelectionEnd({row,col}); }
      else { setSelectionStart({row,col}); setSelectionEnd({row,col}); }
      setActiveCell({row,col});
    }
    }, [editing, activeCell, handleCellDoubleClick, handleEditKeyDown, selectAll, updateSelectedCells, handleCopy, handleCreateTable, handlePaste, toggleBold, toggleItalic, toggleUnderline]);

  useEffect(() => { window.addEventListener('keydown', handleKeyDown); return () => window.removeEventListener('keydown', handleKeyDown); }, [handleKeyDown]);

  // --- Enhanced Border System ---
  const setCellBorders = useCallback((style) => {
    const range = activeRange;
    let obj;
    if (style === 'none') obj = buildBorderObj({preset:'none'});
    else if (style === 'all') obj = buildBorderObj({preset:'all', style: borderLineStyle, color: borderLineColor, width: borderLineWidth});
    else if (style === 'outside') obj = buildBorderObj({preset:'outline', style: borderLineStyle, color: borderLineColor, width: borderLineWidth});
    else if (style === 'inside') obj = buildBorderObj({preset:'inside', style: borderLineStyle, color: borderLineColor, width: borderLineWidth});
    else if (style === 'bottom') obj = buildBorderObj({bottom:true, style: borderLineStyle, color: borderLineColor, width: borderLineWidth});
    else if (style === 'top') obj = buildBorderObj({top:true, style: borderLineStyle, color: borderLineColor, width: borderLineWidth});
    else if (style === 'left') obj = buildBorderObj({left:true, style: borderLineStyle, color: borderLineColor, width: borderLineWidth});
    else if (style === 'right') obj = buildBorderObj({right:true, style: borderLineStyle, color: borderLineColor, width: borderLineWidth});
    else if (style === 'thick') obj = buildBorderObj({preset:'all', width: 3, color: borderLineColor});
    else if (style === 'thickBottom') obj = buildBorderObj({bottom:true, width: 3, color: borderLineColor});
    else if (style === 'double') obj = buildBorderObj({bottom:true, style:'double', color: borderLineColor});
    else if (style === 'diagTR') obj = buildBorderObj({diagTR:true});
    else if (style === 'diagBL') obj = buildBorderObj({diagBL:true});
    else obj = buildBorderObj({preset:'all'});

    setGridData(prev => {
      const next = {...prev};
      for (let r = range.startRow; r <= range.endRow; r++)
        for (let c = range.startCol; c <= range.endCol; c++)
          next[cellId(r,c)] = {...(next[cellId(r,c)] || makeCellData()), ...obj};
      return next;
    });
    setBorderPicker(null);
  }, [activeRange, borderLineStyle, borderLineColor, borderLineWidth]);

  // Advanced border apply
  const applyAdvancedBorder = useCallback(() => {
    const obj = buildBorderObj({
      top: true, bottom: true, left: true, right: true,
      style: borderLineStyle, color: borderLineColor, width: borderLineWidth,
    });
    updateSelectedCells(obj);
    setAdvancedBorderDialog(null);
  }, [updateSelectedCells, borderLineStyle, borderLineColor, borderLineWidth]);

  // --- Merge ---
  const mergeCells = useCallback(() => {
    const range = activeRange;
    if (range.startRow === range.endRow && range.startCol === range.endCol) return;
    setGridData(prev => {
      const next = {...prev};
      for (let r = range.startRow; r <= range.endRow; r++)
        for (let c = range.startCol; c <= range.endCol; c++)
          if (r !== range.startRow || c !== range.startCol) next[cellId(r,c)] = makeCellData();
      return next;
    });
  }, [activeRange]);

  // --- Insert/Delete ---
  const commitPendingEdit = useCallback(() => {
    if (editingRef.current && activeCellRef.current) {
      updateCell(activeCellRef.current.row, activeCellRef.current.col, {value: editValueRef.current});
      setEditing(false);
    }
  }, [updateCell]);

  const insertRowAbove = useCallback(() => {
    commitPendingEdit();
    const ac = activeCellRef.current;
    setGridData(prev => {
      const next = {...prev};
      for (let r = ROWS-1; r > ac.row; r--)
        for (let c = 0; c < COLS; c++) next[cellId(r,c)] = {...(next[cellId(r-1,c)] || makeCellData())};
      for (let c = 0; c < COLS; c++) next[cellId(ac.row,c)] = makeCellData();
      return next;
    });
    setRowHeights(prev => { const n = [...prev]; n.splice(ac.row, 0, DEFAULT_ROW_HEIGHT); n.pop(); return n; });
    // Shift table ranges
    setTables(prev => prev.map(t => t.startRow >= ac.row ? {...t, startRow: t.startRow+1, endRow: t.endRow+1} : t));
  }, [commitPendingEdit]);

  const insertRowBelow = useCallback(() => {
    commitPendingEdit();
    const ac = activeCellRef.current;
    const row = ac.row+1;
    if (row >= ROWS) return;
    setGridData(prev => {
      const next = {...prev};
      for (let r = ROWS-1; r > row; r--)
        for (let c = 0; c < COLS; c++) next[cellId(r,c)] = {...(next[cellId(r-1,c)] || makeCellData())};
      for (let c = 0; c < COLS; c++) next[cellId(row,c)] = makeCellData();
      return next;
    });
    setRowHeights(prev => { const n = [...prev]; n.splice(row, 0, DEFAULT_ROW_HEIGHT); n.pop(); return n; });
    setTables(prev => prev.map(t => t.startRow > ac.row ? {...t, startRow: t.startRow+1, endRow: t.endRow+1} : t));
  }, [commitPendingEdit]);

  const insertColLeft = useCallback(() => {
    commitPendingEdit();
    const ac = activeCellRef.current;
    setGridData(prev => {
      const next = {...prev};
      for (let c = COLS-1; c > ac.col; c--)
        for (let r = 0; r < ROWS; r++) next[cellId(r,c)] = {...(next[cellId(r,c-1)] || makeCellData())};
      for (let r = 0; r < ROWS; r++) next[cellId(r,ac.col)] = makeCellData();
      return next;
    });
    setColWidths(prev => { const n = [...prev]; n.splice(ac.col, 0, DEFAULT_COL_WIDTH); n.pop(); return n; });
    setTables(prev => prev.map(t => t.startCol >= ac.col ? {...t, startCol: t.startCol+1, endCol: t.endCol+1} : t));
  }, [commitPendingEdit]);

  const insertColRight = useCallback(() => {
    commitPendingEdit();
    const ac = activeCellRef.current;
    const col = ac.col+1;
    if (col >= COLS) return;
    setGridData(prev => {
      const next = {...prev};
      for (let c = COLS-1; c > col; c--)
        for (let r = 0; r < ROWS; r++) next[cellId(r,c)] = {...(next[cellId(r,c-1)] || makeCellData())};
      for (let r = 0; r < ROWS; r++) next[cellId(r,col)] = makeCellData();
      return next;
    });
    setColWidths(prev => { const n = [...prev]; n.splice(col, 0, DEFAULT_COL_WIDTH); n.pop(); return n; });
    setTables(prev => prev.map(t => t.startCol > ac.col ? {...t, startCol: t.startCol+1, endCol: t.endCol+1} : t));
  }, [commitPendingEdit]);

  const deleteRow = useCallback(() => {
    commitPendingEdit();
    const ac = activeCellRef.current;
    setGridData(prev => {
      const next = {...prev};
      for (let r = ac.row; r < ROWS-1; r++)
        for (let c = 0; c < COLS; c++) next[cellId(r,c)] = {...(next[cellId(r+1,c)] || makeCellData())};
      for (let c = 0; c < COLS; c++) next[cellId(ROWS-1,c)] = makeCellData();
      return next;
    });
    setTables(prev => prev.map(t => t.startRow > ac.row ? {...t, startRow: t.startRow-1, endRow: t.endRow-1} : t.endRow >= ac.row ? {...t, endRow: t.endRow-1} : t));
  }, [commitPendingEdit]);

  const deleteCol = useCallback(() => {
    commitPendingEdit();
    const ac = activeCellRef.current;
    setGridData(prev => {
      const next = {...prev};
      for (let c = ac.col; c < COLS-1; c++)
        for (let r = 0; r < ROWS; r++) next[cellId(r,c)] = {...(next[cellId(r,c+1)] || makeCellData())};
      for (let r = 0; r < ROWS; r++) next[cellId(r,COLS-1)] = makeCellData();
      return next;
    });
    setTables(prev => prev.map(t => t.startCol > ac.col ? {...t, startCol: t.startCol-1, endCol: t.endCol-1} : t.endCol >= ac.col ? {...t, endCol: t.endCol-1} : t));
  }, [commitPendingEdit]);

  const clearCells = useCallback(() => { commitPendingEdit(); updateSelectedCells(makeCellData()); }, [updateSelectedCells, commitPendingEdit]);

  // --- Resize ---
  const handleColResizeStart = useCallback((e, col) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX, startW = colWidths[col];
    colResizing.current = {col, startX, startW};
    const mm = ev => { if (!colResizing.current) return; const diff = ev.clientX - colResizing.current.startX; const newW = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, colResizing.current.startW + diff)); setColWidths(prev => {const n=[...prev]; n[col]=newW; return n;}); };
    const mu = () => { colResizing.current = null; window.removeEventListener('mousemove', mm); window.removeEventListener('mouseup', mu); };
    window.addEventListener('mousemove', mm); window.addEventListener('mouseup', mu);
  }, [colWidths]);

  const handleRowResizeStart = useCallback((e, row) => {
    e.preventDefault(); e.stopPropagation();
    const startY = e.clientY, startH = rowHeights[row];
    rowResizing.current = {row, startY, startH};
    const mm = ev => { if (!rowResizing.current) return; const diff = ev.clientY - rowResizing.current.startY; const newH = Math.max(MIN_ROW_HEIGHT, Math.min(MAX_ROW_HEIGHT, rowResizing.current.startH + diff)); setRowHeights(prev => {const n=[...prev]; n[row]=newH; return n;}); };
    const mu = () => { rowResizing.current = null; window.removeEventListener('mousemove', mm); window.removeEventListener('mouseup', mu); };
    window.addEventListener('mousemove', mm); window.addEventListener('mouseup', mu);
  }, [rowHeights]);

  // --- Context Menu ---
  const handleContextMenu = useCallback((e, row, col) => {
    e.preventDefault();
    if (!isSelected(row, col)) { setActiveCell({row,col}); setSelectionStart({row,col}); setSelectionEnd({row,col}); }
    setContextMenu({x: e.clientX, y: e.clientY, row, col});
  }, [isSelected]);

  useEffect(() => { const h = () => setContextMenu(null); document.addEventListener('click', h); return () => document.removeEventListener('click', h); }, []);

  // --- Close pickers ---
  useEffect(() => {
    const h = (e) => {
      const picker = document.querySelector('.excel-color-picker-dropdown, .excel-border-picker, .excel-advanced-border, .excel-context-menu, .excel-filter-dropdown, .excel-table-dialog, .excel-cf-dialog');
      if (picker && picker !== e.target && !picker.contains(e.target)) {
        setContextMenu(null); setTextColorPicker(null); setFillColorPicker(null);
        setBorderPicker(null); setFilterDropdown(null);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // --- Fill handle ---
  const handleFillMouseDown = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    fillDragging.current = true;
    const mm = () => {};
    const mu = () => {
      fillDragging.current = false;
      const range = activeRange;
      const fillData = getFillValues(range, gridData);
      setGridData(prev => {
        const next = {...prev};
        const h = range.endRow - range.startRow + 1;
        for (let r = range.endRow+1; r <= range.endRow+h && r < ROWS; r++)
          for (let c = range.startCol; c <= range.endCol; c++)
            next[cellId(r,c)] = {...fillData[(r - range.endRow - 1) % h][c - range.startCol]};
        return next;
      });
      window.removeEventListener('mousemove', mm); window.removeEventListener('mouseup', mu);
    };
    window.addEventListener('mousemove', mm); window.addEventListener('mouseup', mu);
  }, [activeRange, gridData]);

  // --- Formula bar ---
  const formulaValue = useMemo(() => {
    if (editing) return editValue;
    if (activeCell) return getCellData(activeCell.row, activeCell.col).value;
    return '';
  }, [editing, editValue, activeCell, getCellData]);

  const formulaRef = useMemo(() => activeCell ? `${colLabel(activeCell.col)}${activeCell.row+1}` : '', [activeCell]);

  // --- Sort / Filter ---
  const handleSortColumn = useCallback((col, dir) => {
    setSorts(prev => {
      const existing = prev.findIndex(s => s.col === col);
      if (existing >= 0) {
        const n = [...prev];
        if (n[existing].dir === dir) n.splice(existing, 1);
        else n[existing] = {...n[existing], dir};
        return n;
      }
      return [...prev, {col, dir}];
    });
  }, []);

  const handleFilterChange = useCallback((col, op, value, value2) => {
    setFilters(prev => {
      const n = {...prev};
      if (!op) { delete n[col]; return n; }
      n[col] = {op, value, value2};
      return n;
    });
    setFilterDropdown(null);
  }, []);

  // --- Conditional Formatting ---
  const addCFRule = useCallback((rule) => {
    setCfRules(prev => [...prev, rule]);
    setCfDialog(null);
  }, []);

  const removeCFRule = useCallback((idx) => {
    setCfRules(prev => prev.filter((_, i) => i !== idx));
  }, []);

  // --- Sort/Filter row ordering ---
  const sortedFilteredRowOrder = useMemo(() => {
    let rows = [];
    for (let r = 0; r < ROWS; r++) {
      const rowData = {};
      for (let c = 0; c < COLS; c++) rowData[c] = getCellData(r, c);
      rows.push(rowData);
    }
    // Filter
    const filterArr = Object.entries(filters).map(([col, f]) => ({col: parseInt(col), ...f}));
    if (filterArr.length > 0) rows = filterRows(rows, filterArr);
    // Sort
    if (sorts.length > 0) {
      for (const s of sorts) rows = sortRows(rows, s.col, s.dir);
    }
    return rows.map(row => {
      // Find original row index
      for (let r = 0; r < ROWS; r++) if (getCellData(r, 0) === row[0]) return r;
      return -1;
    }).filter(r => r >= 0);
  }, [sorts, filters, getCellData]);

  // Should use sorted order? Only for grid display (but keep editing on original data for simplicity)
  const useSortedOrder = sorts.length > 0 || Object.keys(filters).length > 0;

  // --- CF data collections (for duplicateValues, topN, bottomN, data bars, color scales) ---
  const cfAllValues = useMemo(() => {
    const vals = [];
    for (const key in gridData) {
      const v = gridData[key].value;
      if (v !== '' && v !== undefined && v !== null) vals.push(v);
    }
    return vals;
  }, [gridData]);

  const cfAllNums = useMemo(() => {
    const nums = [];
    for (const key in gridData) {
      const n = parseFloat(gridData[key].value);
      if (!isNaN(n)) nums.push(n);
    }
    return nums;
  }, [gridData]);

  const cfNumMin = cfAllNums.length > 0 ? Math.min(...cfAllNums) : 0;
  const cfNumMax = cfAllNums.length > 0 ? Math.max(...cfAllNums) : 100;

  // --- Render grid ---
  const renderGrid = useMemo(() => {
    const avgRowH = rowHeights.reduce((a,b)=>a+b,0)/ROWS || DEFAULT_ROW_HEIGHT;
    const avgColW = colWidths.reduce((a,b)=>a+b,0)/COLS || DEFAULT_COL_WIDTH;
    const startRow = Math.max(0, Math.floor(scrollTop / avgRowH) - 1);
    const endRow = Math.min(ROWS, startRow + visibleRows + 8);
    const startCol = Math.max(0, Math.floor(scrollLeft / avgColW) - 1);
    const endCol = Math.min(COLS, startCol + visibleCols + 8);

    // Use sorted order map
    const rowOrder = useSortedOrder ? sortedFilteredRowOrder : null;

    const rows = [];
    for (let ri = startRow; ri < endRow; ri++) {
      const r = rowOrder ? rowOrder[ri] : ri;
      if (r === undefined || r >= ROWS) continue;
      const cells = [];
      for (let c = startCol; c < endCol; c++) {
        const cell = getCellData(r, c);
        const sel = isSelected(r, c);
        const isActive = activeCell && activeCell.row === r && activeCell.col === c;
        const isEditing = editing && isActive;

        // Table styling
        const ts = getTableStyle(tables, r, c);
        const tableBg = ts && ts.bg ? ts.bg : undefined;
        const tableColor = ts && ts.color ? ts.color : undefined;
        const isTableHeader = ts && ts.isHeader;
        const isTableTotal = ts && ts.isTotal;

        // Conditional formatting (with proper allValues/allNums collections)
        const activeRule = cfRules.length > 0 ? evaluateCF(cell, cfRules, r * COLS + c, cfAllValues, cfAllNums) : null;
        const dataBar = activeRule && activeRule.type === 'dataBar' ? getDataBarStyle(cell, cfRules, cfNumMin, cfNumMax) : null;
        const colorScale = activeRule && activeRule.type === 'colorScale' ? getColorScaleStyle(cell, cfRules, cfNumMin, cfNumMax) : null;
        const iconSet = activeRule && activeRule.type === 'iconSet' ? getIconSetStyle(cell, cfRules) : null;

        const highlightBg = activeRule && activeRule.type === 'highlight' ? (activeRule.params?.bgColor || '') : '';
        const highlightColor = activeRule && activeRule.type === 'highlight' ? (activeRule.params?.color || '') : '';
        const bgColor = colorScale ? colorScale.backgroundColor : (highlightBg || tableBg || cell.bgColor || undefined);

        const cellStyle = {
          width: colWidths[c], height: rowHeights[r],
          fontFamily: cell.fontFamily, fontSize: cell.fontSize,
          fontWeight: cell.bold ? 'bold' : 'normal',
          fontStyle: cell.italic ? 'italic' : 'normal',
          textDecoration: cell.underline ? 'underline' : cell.strike ? 'line-through' : 'none',
          color: highlightColor || tableColor || cell.color,
          backgroundColor: bgColor,
          whiteSpace: cell.wrapText ? 'normal' : 'nowrap',
          wordBreak: cell.wrapText ? 'break-word' : undefined,
          ...getHAlign(cell.hAlign), ...getVAlign(cell.vAlign),
          borderTop: borderCSS(cell, 'top'),
          borderBottom: borderCSS(cell, 'bottom'),
          borderLeft: borderCSS(cell, 'left'),
          borderRight: borderCSS(cell, 'right'),
          backgroundImage: diagonalCSS(cell),
          backgroundBlendMode: 'overlay',
          paddingLeft: 4 + (cell.indent || 0) * 16,
        };

        let className = `excel-cell${sel ? ' selected' : ''}${isActive ? ' active-cell' : ''}${isTableHeader ? ' table-header' : ''}${isTableTotal ? ' table-total' : ''}${!showGridlines ? ' no-gridlines' : ''}${borderTool !== 'none' ? ' border-tool-active' : ''}`;

        // Table header bold
        if (isTableHeader) {
          cellStyle.fontWeight = 'bold';
        }

        // Total row
        if (isTableTotal) {
          cellStyle.fontWeight = 'bold';
        }

        cells.push(
          <div key={c} className={className} style={cellStyle}
            onMouseDown={(e) => handleCellMouseDown(e, r, c)}
            onMouseEnter={() => handleCellMouseEnter(r, c)}
            onDoubleClick={() => handleCellDoubleClick(r, c)}
            onContextMenu={(e) => handleContextMenu(e, r, c)}
          >
            {isEditing ? (
              <input ref={editRef} className="excel-edit-input"
                value={editValue} onChange={handleEditChange}
                onKeyDown={handleEditKeyDown} onBlur={commitEdit}
                style={{fontFamily:cell.fontFamily,fontSize:cell.fontSize,fontWeight:cell.bold?'bold':'normal',fontStyle:cell.italic?'italic':'normal',color:cell.color,whiteSpace:cell.wrapText?'normal':'nowrap'}}
              />
            ) : (
              <span className="excel-cell-text" style={{whiteSpace:cell.wrapText?'normal':'nowrap'}}>
                {formatCellValue(cell.value, cell.numberFormat)}
                {iconSet && iconSet['--cf-icon'] && <span className="excel-cf-icon">{iconSet['--cf-icon']}</span>}
              </span>
            )}
            {isActive && !editing && <div className="excel-fill-handle" onMouseDown={handleFillMouseDown} />}
            {dataBar && <div className="excel-data-bar" style={dataBar} />}
          </div>
        );
      }
      rows.push(<div key={r} className="excel-row" style={{height: rowHeights[r]}}>{cells}</div>);
    }
    return rows;
  }, [scrollLeft, scrollTop, visibleRows, visibleCols, getCellData, isSelected, activeCell, editing, editValue, colWidths, rowHeights, handleCellMouseDown, handleCellMouseEnter, handleCellDoubleClick, handleContextMenu, handleEditChange, handleEditKeyDown, commitEdit, handleFillMouseDown, useSortedOrder, sortedFilteredRowOrder, tables, cfRules, cfAllValues, cfAllNums, cfNumMin, cfNumMax, showGridlines, borderTool]);

  // --- Status calculations ---
  const fullRange = activeRange;
  const statusCalculations = useMemo(() => {
    let sum=0, count=0, numericCount=0, min=Infinity, max=-Infinity;
    for (let r = fullRange.startRow; r <= fullRange.endRow; r++)
      for (let c = fullRange.startCol; c <= fullRange.endCol; c++) {
        const cell = getCellData(r,c); count++;
        const n = parseFloat(cell.value);
        if (!isNaN(n) && cell.value !== '') { numericCount++; sum+=n; if(n<min) min=n; if(n>max) max=n; }
      }
    return { sum: numericCount>0 ? sum.toFixed(2) : '', avg: numericCount>0 ? (sum/numericCount).toFixed(2) : '', count, min: numericCount>0 ? min : '', max: numericCount>0 ? max : '' };
  }, [fullRange, getCellData]);

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',background:'#f0f0f0',fontFamily:"'Segoe UI', Arial, sans-serif",position:'relative'}}>
      {/* Top bar — report mode */}
      {(onBack || onSave) && (
        <div style={{display:'flex',alignItems:'center',gap:8,padding:'4px 12px',background:'#217346',color:'#fff',fontSize:11}}>
          <Icon.FileEarmarkExcel size={16} color="#fff" />
          <span style={{fontWeight:600,fontSize:12}}>Spreadsheet</span>
          <span style={{marginLeft:'auto',fontSize:10,opacity:0.8}}>AKAGARI — </span>
          <div style={{display:'flex',gap:4,marginLeft:12}}>
            <button onClick={saveToApi} style={{display:'flex',alignItems:'center',gap:4,padding:'2px 10px',background:'rgba(255,255,255,0.15)',color:'#fff',border:'1px solid rgba(255,255,255,0.3)',borderRadius:3,cursor:'pointer',fontSize:11}}>
              <Icon.Save size={12} /> Save
            </button>
            <button onClick={onBack} style={{display:'flex',alignItems:'center',gap:4,padding:'2px 10px',background:'rgba(255,255,255,0.15)',color:'#fff',border:'1px solid rgba(255,255,255,0.3)',borderRadius:3,cursor:'pointer',fontSize:11}}>
              <Icon.ArrowLeft size={12} /> Back
            </button>
          </div>
        </div>
      )}
      {/* Ribbon */}
      <div className="excel-ribbon">
        <div className="excel-ribbon-tabs">
          {['home','insert','pageLayout','data','view'].map(tab => (
            <div key={tab} className={`excel-ribbon-tab${activeRibbonTab===tab?' active':''}`} onClick={() => setActiveRibbonTab(tab)} style={{textTransform:'capitalize'}}>
              {tab==='home'?'Home':tab==='insert'?'Insert':tab==='pageLayout'?'Page Layout':tab==='data'?'Data':'View'}
            </div>
          ))}
        </div>

        {activeRibbonTab==='home' && (
          <div className="excel-ribbon-groups">
            {/* Clipboard */}
            <div className="excel-ribbon-group">
              <button className="excel-ribbon-btn" onClick={handleCopy} title="Copy (Ctrl+C)"><span className="excel-ribbon-btn-icon">📋</span><span className="excel-ribbon-btn-label">Copy</span></button>
              <button className="excel-ribbon-btn" onClick={handlePaste} title="Paste (Ctrl+V)"><span className="excel-ribbon-btn-icon">📌</span><span className="excel-ribbon-btn-label">Paste</span></button>
            </div>
            {/* Font */}
            <div className="excel-ribbon-group" style={{paddingBottom:16}}>
              <div style={{display:'flex',gap:2,flexWrap:'wrap',alignItems:'center'}}>
                <select className="excel-ribbon-select" style={{width:80}} value={getCellData(activeCell.row,activeCell.col).fontFamily} onChange={e=>setCellFont(e.target.value)}>{FONTS.map(f=><option key={f} value={f}>{f}</option>)}</select>
                <select className="excel-ribbon-select" style={{width:52}} value={getCellData(activeCell.row,activeCell.col).fontSize} onChange={e=>setCellFontSize(e.target.value)}>{FONT_SIZES.map(s=><option key={s} value={s}>{s}</option>)}</select>
              </div>
              <div style={{display:'flex',gap:1,marginTop:2}}>
                <button className={`excel-ribbon-btn excel-ribbon-btn-small${getCellData(activeCell.row,activeCell.col).bold?' active-btn':''}`} onClick={toggleBold} title="Bold (Ctrl+B)"><span className="excel-ribbon-btn-icon" style={{fontWeight:'bold'}}>B</span></button>
                <button className={`excel-ribbon-btn excel-ribbon-btn-small${getCellData(activeCell.row,activeCell.col).italic?' active-btn':''}`} onClick={toggleItalic} title="Italic (Ctrl+I)"><span className="excel-ribbon-btn-icon" style={{fontStyle:'italic'}}>I</span></button>
                <button className={`excel-ribbon-btn excel-ribbon-btn-small${getCellData(activeCell.row,activeCell.col).underline?' active-btn':''}`} onClick={toggleUnderline} title="Underline (Ctrl+U)"><span className="excel-ribbon-btn-icon" style={{textDecoration:'underline'}}>U</span></button>
                <div className="excel-ribbon-divider" />
                <div className="excel-ribbon-color-btn excel-ribbon-btn excel-ribbon-btn-small" onClick={e=>{e.stopPropagation();setTextColorPicker(textColorPicker?null:{x:e.currentTarget.getBoundingClientRect().left,y:e.currentTarget.getBoundingClientRect().bottom})}} title="Text Color"><span className="excel-ribbon-btn-icon" style={{color:getCellData(activeCell.row,activeCell.col).color}}>A</span><div className="excel-ribbon-color-preview" style={{background:getCellData(activeCell.row,activeCell.col).color}} /></div>
                <div className="excel-ribbon-color-btn excel-ribbon-btn excel-ribbon-btn-small" onClick={e=>{e.stopPropagation();setFillColorPicker(fillColorPicker?null:{x:e.currentTarget.getBoundingClientRect().left,y:e.currentTarget.getBoundingClientRect().bottom})}} title="Fill Color"><span className="excel-ribbon-btn-icon" style={{fontSize:16}}>⬜</span><div className="excel-ribbon-color-preview" style={{background:getCellData(activeCell.row,activeCell.col).bgColor||'transparent'}} /></div>
                <div className="excel-ribbon-divider" />
                <button className={`excel-ribbon-btn excel-ribbon-btn-small${getCellData(activeCell.row,activeCell.col).wrapText?' active-btn':''}`} onClick={toggleWrapText} title="Wrap Text"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>↩</span></button>
              </div>
            </div>
            {/* Alignment */}
            <div className="excel-ribbon-group" style={{paddingBottom:16}}>
              <div style={{display:'flex',gap:1}}>
                <button className={`excel-ribbon-btn excel-ribbon-btn-small${getCellData(activeCell.row,activeCell.col).hAlign==='left'?' active-btn':''}`} onClick={()=>setCellHAlign('left')} title="Align Left"><span className="excel-ribbon-btn-icon">≡</span></button>
                <button className={`excel-ribbon-btn excel-ribbon-btn-small${getCellData(activeCell.row,activeCell.col).hAlign==='center'?' active-btn':''}`} onClick={()=>setCellHAlign('center')} title="Center"><span className="excel-ribbon-btn-icon">☰</span></button>
                <button className={`excel-ribbon-btn excel-ribbon-btn-small${getCellData(activeCell.row,activeCell.col).hAlign==='right'?' active-btn':''}`} onClick={()=>setCellHAlign('right')} title="Align Right"><span className="excel-ribbon-btn-icon">≡</span></button>
              </div>
              <div style={{display:'flex',gap:1,marginTop:2}}>
                <button className={`excel-ribbon-btn excel-ribbon-btn-small${getCellData(activeCell.row,activeCell.col).vAlign==='top'?' active-btn':''}`} onClick={()=>setCellVAlign('top')} title="Align Top"><span className="excel-ribbon-btn-icon">⊤</span></button>
                <button className={`excel-ribbon-btn excel-ribbon-btn-small${getCellData(activeCell.row,activeCell.col).vAlign==='center'?' active-btn':''}`} onClick={()=>setCellVAlign('center')} title="Middle"><span className="excel-ribbon-btn-icon">⟂</span></button>
                <button className={`excel-ribbon-btn excel-ribbon-btn-small${getCellData(activeCell.row,activeCell.col).vAlign==='bottom'?' active-btn':''}`} onClick={()=>setCellVAlign('bottom')} title="Align Bottom"><span className="excel-ribbon-btn-icon">⊥</span></button>
              </div>
              <div className="excel-ribbon-divider" />
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={mergeCells} title="Merge & Center"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>⊞</span><span className="excel-ribbon-btn-label" style={{fontSize:7}}>Merge</span></button>
            </div>
            {/* Number */}
            <div className="excel-ribbon-group" style={{paddingBottom:16}}>
              <select className="excel-ribbon-select" style={{width:90}} value={getCellData(activeCell.row,activeCell.col).numberFormat} onChange={e=>setCellNumberFormat(e.target.value)}>{NUMBER_FORMATS.map(f=><option key={f.value} value={f.value}>{f.label}</option>)}</select>
              <div style={{display:'flex',gap:1,marginTop:2}}>
                <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={()=>setCellNumberFormat('currency')} title="Currency"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>$</span></button>
                <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={()=>setCellNumberFormat('percentage')} title="Percentage"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>%</span></button>
                <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={()=>setCellNumberFormat('number')} title="Number"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>#</span></button>
              </div>
            </div>
            {/* Borders */}
            <div className="excel-ribbon-group" style={{paddingBottom:16}}>
              <div className="excel-ribbon-color-btn excel-ribbon-btn excel-ribbon-btn-small" onClick={e=>{e.stopPropagation();setBorderPicker(borderPicker?null:{x:e.currentTarget.getBoundingClientRect().left,y:e.currentTarget.getBoundingClientRect().bottom})}} title="Borders"><span className="excel-ribbon-btn-icon" style={{fontSize:14}}>⊟</span><span className="excel-ribbon-btn-label">Borders</span></div>
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={()=>setAdvancedBorderDialog({x:100,y:100})} title="Advanced Borders"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>▦</span><span className="excel-ribbon-btn-label">Line Style</span></button>
              <div className="excel-ribbon-divider" />
              <button className={`excel-ribbon-btn excel-ribbon-btn-small${borderTool==='draw'?' active-btn':''}`} onClick={()=>setBorderTool(borderTool==='draw'?'none':'draw')} title="Border Draw (click cells to paint borders)"><span className="excel-ribbon-btn-icon" style={{fontSize:14}}>✏️</span><span className="excel-ribbon-btn-label">Draw</span></button>
              <button className={`excel-ribbon-btn excel-ribbon-btn-small${borderTool==='erase'?' active-btn':''}`} onClick={()=>setBorderTool(borderTool==='erase'?'none':'erase')} title="Border Eraser (click cells to remove borders)"><span className="excel-ribbon-btn-icon" style={{fontSize:14}}>⌫</span><span className="excel-ribbon-btn-label">Erase</span></button>
              <div className="excel-ribbon-divider" />
              <button className={`excel-ribbon-btn excel-ribbon-btn-small${!showGridlines?' active-btn':''}`} onClick={()=>setShowGridlines(!showGridlines)} title="Toggle Gridlines"><span className="excel-ribbon-btn-icon" style={{fontSize:14}}>▦</span><span className="excel-ribbon-btn-label">Grid</span></button>
            </div>
            {/* Cells */}
            <div className="excel-ribbon-group" style={{paddingBottom:16}}>
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={insertRowAbove} title="Insert Row Above"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>⬆</span><span className="excel-ribbon-btn-label">Insert</span></button>
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={deleteRow} title="Delete Row"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>✕</span><span className="excel-ribbon-btn-label">Delete</span></button>
              <div className="excel-ribbon-divider" />
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={clearCells} title="Clear"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>⌫</span><span className="excel-ribbon-btn-label">Clear</span></button>
            </div>
            {/* Tables */}
            <div className="excel-ribbon-group" style={{paddingBottom:16}}>
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={handleCreateTable} title="Create Table (Ctrl+T)"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>⊞</span><span className="excel-ribbon-btn-label">Table</span></button>
            </div>
            {/* Conditional Formatting */}
            <div className="excel-ribbon-group" style={{paddingBottom:16}}>
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={()=>setCfDialog({type:'highlight'})} title="Conditional Formatting"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>🎨</span><span className="excel-ribbon-btn-label">Cond Format</span></button>
            </div>
          </div>
        )}

        {activeRibbonTab==='insert' && (
          <div className="excel-ribbon-groups">
            <div className="excel-ribbon-group">
              <button className="excel-ribbon-btn" onClick={insertRowAbove} title="Insert Row"><span className="excel-ribbon-btn-icon">⬆</span><span className="excel-ribbon-btn-label">Insert Row</span></button>
              <button className="excel-ribbon-btn" onClick={insertColLeft} title="Insert Column"><span className="excel-ribbon-btn-icon">⬅</span><span className="excel-ribbon-btn-label">Insert Col</span></button>
            </div>
            <div className="excel-ribbon-group">
              <button className="excel-ribbon-btn" onClick={handleCreateTable} title="Create Table (Ctrl+T)"><span className="excel-ribbon-btn-icon">⊞</span><span className="excel-ribbon-btn-label">Table</span></button>
            </div>
          </div>
        )}

        {activeRibbonTab==='data' && (
          <div className="excel-ribbon-groups">
            <div className="excel-ribbon-group">
              <button className="excel-ribbon-btn" onClick={() => handleSortColumn(activeCell.col, SORT_ASC)} title="Sort A-Z"><span className="excel-ribbon-btn-icon">⬇</span><span className="excel-ribbon-btn-label">Sort A-Z</span></button>
              <button className="excel-ribbon-btn" onClick={() => handleSortColumn(activeCell.col, SORT_DESC)} title="Sort Z-A"><span className="excel-ribbon-btn-icon">⬆</span><span className="excel-ribbon-btn-label">Sort Z-A</span></button>
            </div>
            <div className="excel-ribbon-group">
              <button className="excel-ribbon-btn" onClick={() => setFilterDropdown({col: activeCell.col, x: 200, y: 200})} title="Filter"><span className="excel-ribbon-btn-icon">▼</span><span className="excel-ribbon-btn-label">Filter</span></button>
            </div>
            <div className="excel-ribbon-group">
              <button className="excel-ribbon-btn" onClick={() => { setSorts([]); setFilters({}); }} title="Clear"><span className="excel-ribbon-btn-icon">⌫</span><span className="excel-ribbon-btn-label">Clear</span></button>
            </div>
          </div>
        )}

        {activeRibbonTab==='view' && (
          <div className="excel-ribbon-groups">
            <div className="excel-ribbon-group">
              <span style={{fontSize:11,color:'#666',padding:'0 8px'}}>Zoom: {zoom}%</span>
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={() => setZoom(Math.min(200, zoom+10))} title="Zoom In"><span className="excel-ribbon-btn-icon">+</span></button>
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={() => setZoom(Math.max(50, zoom-10))} title="Zoom Out"><span className="excel-ribbon-btn-icon">−</span></button>
            </div>
          </div>
        )}

        {activeRibbonTab==='pageLayout' && (
          <div className="excel-ribbon-groups">
            <div className="excel-ribbon-group">
              <span style={{fontSize:11,color:'#666',padding:'0 8px'}}>Page Layout settings</span>
            </div>
          </div>
        )}

        {/* Table Design tab (contextual) */}
        {showTableDesign && tables.length > 0 && (
          <div className="excel-ribbon-groups" style={{background:'#e8f0fe'}}>
            <div className="excel-ribbon-group">
              <span style={{fontSize:11,fontWeight:600,color:'#2F5496',padding:'0 8px'}}>Table Design</span>
            </div>
            <div className="excel-ribbon-group">
              <span style={{fontSize:11,padding:'0 4px',color:'#2F5496'}}>{tables.length > 0 ? tables[0].name : ''}</span>
            </div>
            <div className="excel-ribbon-group">
              <select className="excel-ribbon-select" style={{width:120}} value={tables[isInTable(tables, activeCell.row, activeCell.col)]?.style || tables[0]?.style || 'light1'}
                onChange={e => { const idx = isInTable(tables, activeCell.row, activeCell.col); if (idx >= 0) applyTableStyle(idx, e.target.value); }}>
                {TABLE_STYLE_NAMES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="excel-ribbon-group">
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={() => {const idx = isInTable(tables, activeCell.row, activeCell.col); if (idx >= 0) setTables(prev => {const n=[...prev]; const t={...n[idx],bandedRows:!n[idx].bandedRows}; n[idx]=t; return n;});}} title="Toggle Banded Rows"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>▦</span><span className="excel-ribbon-btn-label">Banded</span></button>
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={() => {const idx = isInTable(tables, activeCell.row, activeCell.col); if (idx >= 0) setTables(prev => {const n=[...prev]; const t={...n[idx],totalRow:!n[idx].totalRow}; n[idx]=t; return n;});}} title="Toggle Total Row"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>∑</span><span className="excel-ribbon-btn-label">Total</span></button>
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={() => {const idx = isInTable(tables, activeCell.row, activeCell.col); if (idx >= 0) convertTableToRange(idx);}} title="Convert to Range"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>↩</span><span className="excel-ribbon-btn-label">Convert</span></button>
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={() => {const idx = isInTable(tables, activeCell.row, activeCell.col); if (idx >= 0) {setTables(prev => removeTableDuplicates(prev, idx, (r,c) => getCellData(r,c).value));}}} title="Remove Duplicates"><span className="excel-ribbon-btn-icon" style={{fontSize:12}}>⊝</span><span className="excel-ribbon-btn-label">Dedup</span></button>
            </div>
          </div>
        )}
      </div>

      {/* Formula Bar */}
      <div className="excel-formula-bar">
        <div className="excel-formula-ref">{formulaRef}</div>
        <div className="excel-formula-fx">fx</div>
        <input className="excel-formula-input" value={formulaValue}
          onChange={e => {setEditValue(e.target.value); if (!editing && activeCell) setEditing(true);}}
          onKeyDown={e => {if(e.key==='Enter'){e.preventDefault();if(activeCell)updateCell(activeCell.row,activeCell.col,{value:editValue});setEditing(false);setStatusText('Ready');}else if(e.key==='Escape'){e.preventDefault();setEditing(false);setStatusText('Ready');}}}
          onFocus={() => {if(!editing){setEditing(true);setStatusText('Edit');}}}
        />
      </div>

      {/* Grid */}
      <div className="excel-grid-container" ref={containerRef}>
        <div className="excel-grid-scroll" ref={gridRef} onScroll={handleScroll}>
          <div style={{position:'relative',minWidth:ROW_HEADER_W + COLS * DEFAULT_COL_WIDTH,minHeight:HEADER_H + ROWS * DEFAULT_ROW_HEIGHT}}>
            <div className="excel-corner-btn" onClick={selectAll} />
            <div className="excel-col-headers" style={{marginLeft:ROW_HEADER_W}}>
              {Array.from({length:Math.min(COLS,visibleCols+5)},(_,i)=>{
                const c=i+Math.floor(scrollLeft/DEFAULT_COL_WIDTH);
                if(c>=COLS) return null;
                const isSelectedCol = activeRange.startCol <= c && activeRange.endCol >= c;
                const sortDir = sorts.find(s => s.col === c);
                const hasFilter = filters[c] !== undefined;
                return (
                  <div key={c} className={`excel-col-header${isSelectedCol?' selected':''}`} style={{width:colWidths[c]}}
                    onClick={() => {setSelectionStart({row:0,col:c});setSelectionEnd({row:ROWS-1,col:c});setActiveCell({row:0,col:c});}}>
                    {colLabel(c)}
                    {sortDir && <span className="excel-sort-indicator">{sortDir.dir===SORT_ASC?' ↑':' ↓'}</span>}
                    {hasFilter && <span className="excel-filter-indicator">▼</span>}
                    <div className="excel-col-header-btns">
                      <span className="excel-col-sort-btn" onClick={e=>{e.stopPropagation();handleSortColumn(c, SORT_ASC);}}>↑</span>
                      <span className="excel-col-filter-btn" onClick={e=>{e.stopPropagation();setFilterDropdown({col:c,x:e.clientX,y:e.clientY});}}>▼</span>
                    </div>
                    <div className="excel-col-resize" onMouseDown={e=>handleColResizeStart(e,c)} />
                  </div>
                );
              })}
            </div>
            <div style={{display:'flex'}}>
              <div className="excel-row-header-area">
                {Array.from({length:Math.min(ROWS,visibleRows+5)},(_,i)=>{
                  const r=i+Math.floor(scrollTop/DEFAULT_ROW_HEIGHT);
                  if(r>=ROWS) return null;
                  const isSelectedRow = activeRange.startRow <= r && activeRange.endRow >= r;
                  return (
                    <div key={r} className={`excel-row-header${isSelectedRow?' selected':''}`} style={{width:ROW_HEADER_W,height:rowHeights[r]}}
                      onClick={() => {setSelectionStart({row:r,col:0});setSelectionEnd({row:r,col:COLS-1});setActiveCell({row:r,col:0});}}>
                      {r+1}
                      <div className="excel-row-resize" onMouseDown={e=>handleRowResizeStart(e,r)} />
                    </div>
                  );
                })}
              </div>
              <div className="excel-grid-area" style={{position:'relative'}}>{renderGrid}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Sheet tabs */}
      <div className="excel-sheet-bar">
        <div className="excel-sheet-scroll"><button className="excel-sheet-scroll-btn">◀</button><button className="excel-sheet-scroll-btn">▶</button></div>
        <div className="excel-sheet-list">
          {sheets.map((sheet,i)=>(
            <div key={sheet.id} className={`excel-sheet-tab${i===activeSheet?' active':''}`} onClick={()=>setActiveSheet(i)}><span className="sheet-icon">📄</span>{sheet.name}</div>
          ))}
        </div>
        <button className="excel-add-sheet-btn" onClick={()=>{setSheets(prev=>[...prev,{name:`Sheet${prev.length+1}`,id:prev.length}]);setActiveSheet(sheets.length);}} title="Add new sheet">+</button>
      </div>

      {/* Status bar */}
      <div className="excel-status-bar">
        <div className="excel-status-mode"><span style={{fontWeight:600}}>{statusText}</span></div>
        <div className="excel-status-calc">
          {statusCalculations.count > 1 && (<>
            <div className="excel-status-calc-item"><span className="excel-status-calc-label">Average</span><span className="excel-status-calc-value">{statusCalculations.avg}</span></div>
            <div className="excel-status-calc-item"><span className="excel-status-calc-label">Count</span><span className="excel-status-calc-value">{statusCalculations.count}</span></div>
            <div className="excel-status-calc-item"><span className="excel-status-calc-label">Sum</span><span className="excel-status-calc-value">{statusCalculations.sum}</span></div>
          </>)}
        </div>
        <div className="excel-status-view"><span>Normal</span></div>
        <div className="excel-status-zoom"><span>{zoom}%</span></div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div className="excel-context-menu" ref={contextMenuRef} style={{left:contextMenu.x,top:contextMenu.y}}>
          <div className="excel-context-menu-item" onClick={()=>{handleCopy();setContextMenu(null);}}><span className="ctx-icon">📋</span>Copy<span className="ctx-shortcut">Ctrl+C</span></div>
          <div className="excel-context-menu-item" onClick={()=>{handlePaste();setContextMenu(null);}}><span className="ctx-icon">📌</span>Paste<span className="ctx-shortcut">Ctrl+V</span></div>
          <div className="excel-context-menu-divider" />
          <div className="excel-context-menu-item" onClick={()=>{insertRowAbove();setContextMenu(null);}}><span className="ctx-icon">⬆</span>Insert Row Above</div>
          <div className="excel-context-menu-item" onClick={()=>{insertRowBelow();setContextMenu(null);}}><span className="ctx-icon">⬇</span>Insert Row Below</div>
          <div className="excel-context-menu-item" onClick={()=>{insertColLeft();setContextMenu(null);}}><span className="ctx-icon">⬅</span>Insert Column Left</div>
          <div className="excel-context-menu-item" onClick={()=>{insertColRight();setContextMenu(null);}}><span className="ctx-icon">➡</span>Insert Column Right</div>
          <div className="excel-context-menu-divider" />
          <div className="excel-context-menu-item" onClick={()=>{deleteRow();setContextMenu(null);}}><span className="ctx-icon">✕</span>Delete Row</div>
          <div className="excel-context-menu-item" onClick={()=>{deleteCol();setContextMenu(null);}}><span className="ctx-icon">✕</span>Delete Column</div>
          <div className="excel-context-menu-divider" />
          <div className="excel-context-menu-item" onClick={()=>{clearCells();setContextMenu(null);}}><span className="ctx-icon">⌫</span>Clear Contents</div>
          <div className="excel-context-menu-divider" />
          <div className="excel-context-menu-item" onClick={()=>{handleCreateTable();setContextMenu(null);}}><span className="ctx-icon">⊞</span>Create Table</div>
        </div>
      )}

      {/* Color Pickers */}
      {textColorPicker && (
        <div className="excel-color-picker-dropdown" style={{left:textColorPicker.x,top:textColorPicker.y}}>
          <div className="excel-color-grid">{THEME_COLORS.map((clr,i)=><div key={i} className="excel-color-swatch" style={{backgroundColor:clr,border:clr==='#FFFFFF'?'1px solid #ccc':undefined}} onClick={e=>{e.stopPropagation();setCellColor(clr);}} />)}</div>
        </div>
      )}
      {fillColorPicker && (
        <div className="excel-color-picker-dropdown" style={{left:fillColorPicker.x,top:fillColorPicker.y}}>
          <div className="excel-color-grid">{THEME_COLORS.map((clr,i)=><div key={i} className="excel-color-swatch" style={{backgroundColor:clr,border:clr==='#FFFFFF'?'1px solid #ccc':undefined}} onClick={e=>{e.stopPropagation();setCellBgColor(clr);}} />)}</div>
        </div>
      )}

      {/* Border Picker */}
      {borderPicker && (
        <div className="excel-border-picker" style={{left:borderPicker.x,top:borderPicker.y}}>
          <div className="excel-border-picker-header">
            <label>Style: <select value={borderLineStyle} onChange={e=>setBorderLineStyle(e.target.value)}>{BORDER_STYLES.map(s=><option key={s} value={s}>{s}</option>)}</select></label>
            <label>Color: <input type="color" value={borderLineColor} onChange={e=>setBorderLineColor(e.target.value)} /></label>
            <label>W: <select value={borderLineWidth} onChange={e=>setBorderLineWidth(parseInt(e.target.value))}>{BORDER_WIDTHS.map(w=><option key={w} value={w}>{w}px</option>)}</select></label>
          </div>
          <div className="excel-border-picker-grid">
            {BORDER_UI.map((opt,i)=>(
              <div key={i} className={`excel-border-btn${opt.diag?' excel-border-btn-diag':''}`}
                onClick={e=>{e.stopPropagation();
                  if (opt.preset) setCellBorders(opt.preset);
                  else if (opt.dir) setCellBorders(opt.dir);
                  else if (opt.diag==='TR') setCellBorders('diagTR');
                  else if (opt.diag==='BL') setCellBorders('diagBL');
                }}
                title={opt.label}><span style={{fontSize:16}}>{opt.icon}</span></div>
            ))}
          </div>
        </div>
      )}

      {/* Advanced Border Dialog */}
      {advancedBorderDialog && (
        <div className="excel-advanced-border" style={{position:'fixed',left:'50%',top:'50%',transform:'translate(-50%,-50%)',zIndex:10002,background:'white',border:'1px solid #c8c8c8',borderRadius:8,padding:16,boxShadow:'0 8px 32px rgba(0,0,0,0.2)',minWidth:280}}>
          <h3 style={{margin:'0 0 12px',fontSize:14,fontFamily:'Segoe UI'}}>Advanced Border</h3>
          <div style={{display:'flex',flexDirection:'column',gap:8,fontSize:12}}>
            <label>Line Style: <select value={borderLineStyle} onChange={e=>setBorderLineStyle(e.target.value)} style={{marginLeft:8}}>{BORDER_STYLES.map(s=><option key={s} value={s}>{s}</option>)}</select></label>
            <label>Color: <input type="color" value={borderLineColor} onChange={e=>setBorderLineColor(e.target.value)} style={{marginLeft:8,verticalAlign:'middle'}} /></label>
            <label>Width: <select value={borderLineWidth} onChange={e=>setBorderLineWidth(parseInt(e.target.value))} style={{marginLeft:8}}>{BORDER_WIDTHS.map(w=><option key={w} value={w}>{w}px</option>)}</select></label>
            <div style={{display:'flex',gap:4,marginTop:8}}>
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={()=>applyAdvancedBorder()} style={{flex:1}}>Apply</button>
              <button className="excel-ribbon-btn excel-ribbon-btn-small" onClick={()=>setAdvancedBorderDialog(null)} style={{flex:1}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Table Dialog */}
      {tableDialog && (
        <div className="excel-table-dialog" style={{position:'fixed',left:'50%',top:'50%',transform:'translate(-50%,-50%)',zIndex:10002,background:'white',border:'1px solid #c8c8c8',borderRadius:8,padding:20,boxShadow:'0 8px 32px rgba(0,0,0,0.2)',minWidth:320}}>
          <h3 style={{margin:'0 0 16px',fontSize:15,fontFamily:'Segoe UI'}}>Create Table</h3>
          <div style={{display:'flex',flexDirection:'column',gap:10,fontSize:13}}>
            <div>Range: {colLabel(tableDialog.startCol)}{tableDialog.startRow+1}:{colLabel(tableDialog.endCol)}{tableDialog.endRow+1}</div>
            <label>Table Name: <input value={tableDialog.name} onChange={e=>setTableDialog({...tableDialog,name:e.target.value})} style={{marginLeft:8,padding:'2px 6px',border:'1px solid #ccc',borderRadius:2}} /></label>
            <label>Style: <select value={tableDialog.style} onChange={e=>setTableDialog({...tableDialog,style:e.target.value})} style={{marginLeft:8}}>{TABLE_STYLE_NAMES.map(s=><option key={s} value={s}>{s}</option>)}</select></label>
            <label><input type="checkbox" checked={tableDialog.headerRow} onChange={e=>setTableDialog({...tableDialog,headerRow:e.target.checked})} /> My table has headers</label>
            <div style={{display:'flex',gap:8,marginTop:8}}>
              <button onClick={confirmCreateTable} style={{flex:1,padding:'6px 16px',background:'#0078d7',color:'white',border:'none',borderRadius:3,cursor:'pointer'}}>OK</button>
              <button onClick={()=>setTableDialog(null)} style={{flex:1,padding:'6px 16px',background:'#f0f0f0',border:'1px solid #ccc',borderRadius:3,cursor:'pointer'}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Dropdown */}
      {filterDropdown && (
        <div className="excel-filter-dropdown" style={{position:'fixed',left:filterDropdown.x,top:filterDropdown.y,zIndex:10001,background:'white',border:'1px solid #c8c8c8',borderRadius:6,padding:12,boxShadow:'0 4px 20px rgba(0,0,0,0.2)',minWidth:200}}>
          <div style={{fontSize:12,fontWeight:600,marginBottom:8,fontFamily:'Segoe UI'}}>Filter Column {colLabel(filterDropdown.col)}</div>
          <div style={{display:'flex',flexDirection:'column',gap:6,fontSize:12}}>
            <button className="excel-filter-option" onClick={()=>handleFilterChange(filterDropdown.col,'textContains','')}>Clear Filter</button>
            <div className="excel-filter-divider" />
            <input placeholder="Contains..." style={{padding:'3px 6px',border:'1px solid #ccc',borderRadius:2}} onChange={e=>handleFilterChange(filterDropdown.col,'textContains',e.target.value)} />
            <input placeholder="Greater than..." style={{padding:'3px 6px',border:'1px solid #ccc',borderRadius:2}} onChange={e=>handleFilterChange(filterDropdown.col,'numberGreater',e.target.value)} />
            <input placeholder="Less than..." style={{padding:'3px 6px',border:'1px solid #ccc',borderRadius:2}} onChange={e=>handleFilterChange(filterDropdown.col,'numberLess',e.target.value)} />
            <button className="excel-filter-option" onClick={()=>handleFilterChange(filterDropdown.col,'dateToday','')}>Today</button>
            <button className="excel-filter-option" onClick={()=>handleFilterChange(filterDropdown.col,'dateThisMonth','')}>This Month</button>
          </div>
        </div>
      )}

      {/* Conditional Formatting Dialog */}
      {cfDialog && (
        <div className="excel-cf-dialog" style={{position:'fixed',left:'50%',top:'50%',transform:'translate(-50%,-50%)',zIndex:10002,background:'white',border:'1px solid #c8c8c8',borderRadius:8,padding:20,boxShadow:'0 8px 32px rgba(0,0,0,0.2)',minWidth:320,maxHeight:'80vh',overflowY:'auto'}}>
          <h3 style={{margin:'0 0 12px',fontSize:15,fontFamily:'Segoe UI'}}>Conditional Formatting</h3>
          {cfRules.length > 0 && (
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:600,marginBottom:4}}>Current Rules:</div>
              {cfRules.map((rule,i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'3px 0',fontSize:12,borderBottom:'1px solid #eee'}}>
                  <span>{rule.type} - {rule.params?.op || ''}</span>
                  <button onClick={()=>removeCFRule(i)} style={{background:'none',border:'none',cursor:'pointer',color:'#c00'}}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div style={{display:'flex',flexDirection:'column',gap:8,fontSize:12}}>
            <div style={{fontWeight:600}}>Add Rule:</div>
            <button className="excel-ribbon-btn excel-ribbon-btn-small" style={{justifyContent:'flex-start',padding:'4px 12px'}} onClick={()=>addCFRule(cfRule('highlight',{op:'greaterThan',value:'0',bgColor:'#FFC7CE',color:'#9C0006'}))}>Greater Than 0</button>
            <button className="excel-ribbon-btn excel-ribbon-btn-small" style={{justifyContent:'flex-start',padding:'4px 12px'}} onClick={()=>addCFRule(cfRule('highlight',{op:'lessThan',value:'0',bgColor:'#C6EFCE',color:'#006100'}))}>Less Than 0</button>
            <button className="excel-ribbon-btn excel-ribbon-btn-small" style={{justifyContent:'flex-start',padding:'4px 12px'}} onClick={()=>addCFRule(cfRule('highlight',{op:'equalTo',value:'0',bgColor:'#FFEB9C',color:'#9C6500'}))}>Equal To 0</button>
            <button className="excel-ribbon-btn excel-ribbon-btn-small" style={{justifyContent:'flex-start',padding:'4px 12px'}} onClick={()=>addCFRule(cfRule('highlight',{op:'textContains',value:'test',bgColor:'#BDD7EE',color:'#1F4E79'}))}>Text Contains</button>
            <button className="excel-ribbon-btn excel-ribbon-btn-small" style={{justifyContent:'flex-start',padding:'4px 12px'}} onClick={()=>addCFRule(cfRule('highlight',{op:'duplicateValues',bgColor:'#FCE4D6',color:'#C65911'}))}>Duplicate Values</button>
            <button className="excel-ribbon-btn excel-ribbon-btn-small" style={{justifyContent:'flex-start',padding:'4px 12px'}} onClick={()=>addCFRule(cfRule('highlight',{op:'topN',n:10,bgColor:'#D9E2F3',color:'#2F5496'}))}>Top 10</button>
            <button className="excel-ribbon-btn excel-ribbon-btn-small" style={{justifyContent:'flex-start',padding:'4px 12px'}} onClick={()=>addCFRule(cfRule('highlight',{op:'bottomN',n:10,bgColor:'#E2EFDA',color:'#375623'}))}>Bottom 10</button>
            <button className="excel-ribbon-btn excel-ribbon-btn-small" style={{justifyContent:'flex-start',padding:'4px 12px'}} onClick={()=>addCFRule(cfRule('highlight',{op:'dateOccurring',period:'today',bgColor:'#D9E2F3',color:'#2F5496'}))}>Date: Today</button>
            <div style={{borderTop:'1px solid #e0e0e0',margin:'4px 0'}} />
            <button className="excel-ribbon-btn excel-ribbon-btn-small" style={{justifyContent:'flex-start',padding:'4px 12px'}} onClick={()=>addCFRule(cfRule('dataBar',{color:'#5B9BD5'}))}>Data Bar</button>
            <button className="excel-ribbon-btn excel-ribbon-btn-small" style={{justifyContent:'flex-start',padding:'4px 12px'}} onClick={()=>addCFRule(cfRule('colorScale',{colors:['#F8696B','#FFEB84','#63BE7B']}))}>Color Scale (3-color)</button>
            <button className="excel-ribbon-btn excel-ribbon-btn-small" style={{justifyContent:'flex-start',padding:'4px 12px'}} onClick={()=>addCFRule(cfRule('iconSet',{iconType:'arrows',threshold1:67,threshold2:33}))}>Icon Set: Arrows</button>
            <button className="excel-ribbon-btn excel-ribbon-btn-small" style={{justifyContent:'flex-start',padding:'4px 12px'}} onClick={()=>addCFRule(cfRule('iconSet',{iconType:'flags',threshold1:67,threshold2:33}))}>Icon Set: Flags</button>
          </div>
          <button onClick={()=>setCfDialog(null)} style={{marginTop:12,padding:'6px 16px',background:'#f0f0f0',border:'1px solid #ccc',borderRadius:3,cursor:'pointer',width:'100%'}}>Close</button>
        </div>
      )}
    </div>
  );
}
